import type { MenuEntry, MenuGroup } from './WordMenuBar';
import type { InsertKind } from './presentation/SlideInsertDialogs';
import { BG_PRESETS, LAYOUTS, THEME_PRESETS } from '@/lib/slideDeck';

export interface PowerPointMenuHandlers {
  save: () => void;
  duplicate: () => void;
  rename: () => void;
  exportAs: (format: 'pptx' | 'pdf') => void;
  print: () => void;
  share: () => void;
  history: () => void;
  close: () => void;
  del: () => void;
  canManage: boolean;

  addSlide: () => void;
  duplicateSlide: () => void;
  deleteSlide: () => void;
  moveUp: () => void;
  moveDown: () => void;
  insertLayout: (id: string) => void;

  insert: (kind: InsertKind) => void;
  insertText: () => void;

  align: (a: 'left' | 'center' | 'right') => void;
  setBg: (color: string) => void;
  applyTheme: (id: string) => void;
  layer: (action: 'front' | 'forward' | 'backward' | 'back') => void;
  toggleNotes: () => void;

  settings: { present: boolean; zoom: number; showNotes: boolean; pageFormat: string };
  togglePresent: () => void;
  setZoom: (z: number) => void;
  setPageFormat: (value: string) => void;

  openAi: () => void;
  publicLink: () => void;

  notify: (msg: string) => void;
  showShortcuts: () => void;
  showAbout: () => void;
}

const DIV = { divider: true } as MenuEntry;

export function buildPowerPointMenus(h: PowerPointMenuHandlers): MenuGroup[] {
  return [
    {
      key: 'file',
      label: 'Fichier',
      entries: [
        { label: 'Enregistrer', icon: 'ri-save-line', shortcut: 'Ctrl+S', run: h.save },
        { label: 'Faire une copie', icon: 'ri-file-copy-line', run: h.duplicate },
        { label: 'Renommer le document', icon: 'ri-edit-line', run: h.rename },
        DIV,
        {
          label: 'Exporter',
          icon: 'ri-download-2-line',
          children: [
            { label: 'PowerPoint (.pptx)', icon: 'ri-file-ppt-2-line', run: () => h.exportAs('pptx') },
            { label: 'PDF (.pdf)', icon: 'ri-file-pdf-2-line', run: () => h.exportAs('pdf') },
          ],
        },
        { label: 'Imprimer', icon: 'ri-printer-line', shortcut: 'Ctrl+P', run: h.print },
        DIV,
        { label: 'Créer un lien de présentation', icon: 'ri-link', run: h.publicLink },
        { label: 'Partager avec des membres', icon: 'ri-share-line', run: h.share },
        { label: 'Historique des versions', icon: 'ri-history-line', run: h.history },
        DIV,
        { label: 'Fermer', icon: 'ri-close-line', shortcut: 'Échap', run: h.close },
        ...(h.canManage
          ? [{ label: 'Supprimer le document', icon: 'ri-delete-bin-line', danger: true, run: h.del }]
          : []),
      ],
    },
    {
      key: 'edit',
      label: 'Édition',
      entries: [
        { label: 'Dupliquer la diapositive', icon: 'ri-file-copy-line', shortcut: 'Ctrl+D', run: h.duplicateSlide },
        { label: 'Supprimer la diapositive', icon: 'ri-delete-bin-6-line', run: h.deleteSlide },
        DIV,
        { label: 'Monter la diapositive', icon: 'ri-arrow-up-line', run: h.moveUp },
        { label: 'Descendre la diapositive', icon: 'ri-arrow-down-line', run: h.moveDown },
        DIV,
        {
          label: 'Organiser (axe Z)',
          icon: 'ri-stack-line',
          children: [
            { label: 'Mettre au premier plan', icon: 'ri-bring-to-front', run: () => h.layer('front') },
            { label: 'Avancer', icon: 'ri-arrow-up-line', run: () => h.layer('forward') },
            { label: 'Reculer', icon: 'ri-arrow-down-line', run: () => h.layer('backward') },
            { label: 'Mettre à l\u2019arrière-plan', icon: 'ri-send-to-back', run: () => h.layer('back') },
          ],
        },
      ],
    },
    {
      key: 'insert',
      label: 'Insertion',
      entries: [
        { label: 'Nouvelle diapositive', icon: 'ri-add-line', shortcut: 'Ctrl+M', run: h.addSlide },
        {
          label: 'Nouvelle diapositive avec mise en page',
          icon: 'ri-layout-masonry-line',
          children: LAYOUTS.map((l) => ({ label: l.label, icon: l.icon, run: () => h.insertLayout(l.id) })),
        },
        DIV,
        { label: 'Zone de texte', icon: 'ri-text', run: h.insertText },
        { label: 'Image', icon: 'ri-image-line', run: () => h.insert('image') },
        { label: 'Vidéo', icon: 'ri-video-line', run: () => h.insert('video') },
        { label: 'Tableau', icon: 'ri-table-line', run: () => h.insert('table') },
        { label: 'Graphique', icon: 'ri-bar-chart-box-line', run: () => h.insert('chart') },
        { label: 'Lien', icon: 'ri-link', run: () => h.insert('link') },
      ],
    },
    {
      key: 'format',
      label: 'Format',
      entries: [
        {
          label: 'Alignement du texte',
          icon: 'ri-align-left',
          children: [
            { label: 'À gauche', icon: 'ri-align-left', run: () => h.align('left') },
            { label: 'Centré', icon: 'ri-align-center', run: () => h.align('center') },
            { label: 'À droite', icon: 'ri-align-right', run: () => h.align('right') },
          ],
        },
        {
          label: 'Couleur de fond',
          icon: 'ri-paint-fill',
          grid: 8,
          children: BG_PRESETS.map((c) => ({ label: c, color: c, run: () => h.setBg(c) })),
        },
        DIV,
        {
          label: 'Thème de la présentation',
          icon: 'ri-palette-line',
          children: THEME_PRESETS.map((t) => ({ label: t.label, run: () => h.applyTheme(t.id) })),
        },
      ],
    },
    {
      key: 'view',
      label: 'Affichage',
      entries: [
        {
          label: h.settings.present ? 'Quitter la présentation' : 'Lancer la présentation',
          icon: 'ri-play-circle-line',
          shortcut: 'F5',
          run: h.togglePresent,
        },
        { label: 'Notes du présentateur', icon: 'ri-sticky-note-line', checked: h.settings.showNotes, run: h.toggleNotes },
        DIV,
        {
          label: 'Format de la page',
          icon: 'ri-aspect-ratio-line',
          children: [
            { label: '16:9', checked: h.settings.pageFormat === '960x540', run: () => h.setPageFormat('960x540') },
            { label: '4:3', checked: h.settings.pageFormat === '1024x768', run: () => h.setPageFormat('1024x768') },
            { label: 'Carré', checked: h.settings.pageFormat === '1080x1080', run: () => h.setPageFormat('1080x1080') },
            { label: 'Vertical', checked: h.settings.pageFormat === '720x1280', run: () => h.setPageFormat('720x1280') },
          ],
        },
        {
          label: 'Zoom',
          icon: 'ri-zoom-in-line',
          children: [75, 100, 125, 150].map((z) => ({
            label: `${z} %`,
            checked: h.settings.zoom === z,
            run: () => h.setZoom(z),
          })),
        },
      ],
    },
    {
      key: 'ai',
      label: 'Assistant IA',
      entries: [
        { label: 'Ouvrir le chat IA', icon: 'ri-sparkling-line', run: h.openAi },
        DIV,
        { label: 'Créer une présentation depuis un sujet ou un lien', icon: 'ri-magic-line', run: h.openAi },
      ],
    },
    {
      key: 'help',
      label: 'Aide',
      entries: [
        { label: 'Raccourcis clavier', icon: 'ri-keyboard-line', shortcut: 'Ctrl+/', run: h.showShortcuts },
        { label: 'À propos de l\u2019éditeur', icon: 'ri-question-line', run: h.showAbout },
      ],
    },
  ];
}